/**
 * Color filter utilities
 * Converts ColorGrading values to CSS filter strings for preview
 * and Canvas 2D filter strings for export
 */

import type { ColorGrading, LightSource } from '@frameit/types';

/**
 * Build a CSS filter string from ColorGrading values.
 * Maps artistic controls to available CSS filter functions.
 *
 * CSS filter supports: brightness, contrast, saturate, hue-rotate, sepia, invert, grayscale, blur
 * We approximate temperature/tint/vibrance/highlights/shadows using these.
 */
export function buildCssFilter(grading: ColorGrading | undefined): string {
  if (!grading) return 'none';

  const filters: string[] = [];

  // Contrast: -100..+100 → CSS contrast 0.5..1.5
  const contrast = grading.contrast ?? 0;
  if (contrast !== 0) {
    filters.push(`contrast(${1 + contrast / 100})`);
  }

  // Saturation: -100..+100 → CSS saturate 0..2
  const saturation = grading.saturation ?? 0;
  if (saturation !== 0) {
    filters.push(`saturate(${1 + saturation / 100})`);
  }

  // Temperature: negative = cool (blue shift), positive = warm (yellow/sepia shift)
  // Approximate with hue-rotate + slight sepia for warmth
  const temperature = grading.temperature ?? 0;
  if (temperature !== 0) {
    // Warm temperatures: slight sepia tint
    if (temperature > 0) {
      filters.push(`sepia(${(temperature / 100) * 0.3})`);
      filters.push(`saturate(${1 + (temperature / 100) * 0.2})`);
    } else {
      // Cool temperatures: hue shift towards blue
      filters.push(`hue-rotate(${(temperature / 100) * 20}deg)`);
    }
  }

  // Tint: -100..+100 → hue-rotate for color cast
  const tint = grading.tint ?? 0;
  if (tint !== 0) {
    filters.push(`hue-rotate(${(tint / 100) * 30}deg)`);
  }

  // Highlights: brightens bright areas. Approximate with brightness boost
  const highlights = grading.highlights ?? 0;
  if (highlights !== 0) {
    filters.push(`brightness(${1 + (highlights / 100) * 0.15})`);
  }

  // Shadows: lightens/darkens dark areas. Approximate with brightness
  const shadows = grading.shadows ?? 0;
  if (shadows !== 0) {
    filters.push(`brightness(${1 + (shadows / 100) * 0.1})`);
  }

  // Vibrance: like saturation but affects less-saturated colors more
  // Approximate with saturate at lower intensity
  const vibrance = grading.vibrance ?? 0;
  if (vibrance !== 0) {
    filters.push(`saturate(${1 + (vibrance / 100) * 0.5})`);
  }

  return filters.length > 0 ? filters.join(' ') : 'none';
}

/**
 * Build a Canvas 2D ctx.filter string from ColorGrading values.
 * Canvas 2D supports a subset of CSS filters.
 */
export function buildCanvasFilter(grading: ColorGrading | undefined): string {
  // Canvas 2D ctx.filter uses the same CSS filter syntax
  return buildCssFilter(grading);
}

/**
 * Build a CSS filter string for scene lighting matching.
 * Applies a subtle warm/cool tint to blend the artwork into the room's lighting.
 *
 * @param colorTemp Scene color temperature: 'warm' | 'neutral' | 'cool'
 * @param lightSource Optional light source for intensity-based adjustments
 * @returns CSS filter string
 */
export function buildSceneLightingFilter(
  colorTemp: 'warm' | 'neutral' | 'cool' | undefined,
  lightSource: LightSource | undefined,
): string {
  const filters: string[] = [];

  // Color temperature tint (subtle — 10-15% effect)
  if (colorTemp === 'warm') {
    filters.push('sepia(0.08)');
    filters.push('saturate(1.05)');
    filters.push('brightness(1.02)');
  } else if (colorTemp === 'cool') {
    filters.push('hue-rotate(-5deg)');
    filters.push('saturate(0.97)');
    filters.push('brightness(1.01)');
  }

  // Light intensity adjustments
  if (lightSource) {
    const intensity = lightSource.intensity ?? 0.7;
    if (intensity > 0.9) {
      // Bright scene — slightly lift artwork brightness
      filters.push(`brightness(${1 + (intensity - 0.9) * 0.15})`);
    } else if (intensity < 0.5) {
      // Dim scene — slightly darken artwork to match
      filters.push(`brightness(${1 - (0.5 - intensity) * 0.1})`);
    }
  }

  return filters.length > 0 ? filters.join(' ') : 'none';
}

/**
 * Build a CSS linear-gradient for directional light overlay.
 * Creates a subtle highlight/shadow effect matching the scene's light direction.
 *
 * @param lightSource Scene light source
 * @returns CSS background-image gradient string, or empty string
 */
export function buildLightDirectionOverlay(lightSource: LightSource | undefined): string {
  if (!lightSource?.direction) return '';
  if (lightSource.type === 'ambient') return '';

  const { x, y } = lightSource.direction;
  // Compute angle from direction vector (light comes FROM this direction)
  const angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
  const intensity = (lightSource.intensity ?? 0.7) * 0.08; // Very subtle

  return `linear-gradient(${angle}deg, rgba(255,255,255,${intensity}) 0%, transparent 40%, rgba(0,0,0,${intensity * 0.5}) 100%)`;
}

/**
 * Check if any color grading adjustments are active
 */
export function hasActiveColorGrading(grading: ColorGrading | undefined): boolean {
  if (!grading) return false;
  return (
    (grading.temperature ?? 0) !== 0 ||
    (grading.tint ?? 0) !== 0 ||
    (grading.contrast ?? 0) !== 0 ||
    (grading.saturation ?? 0) !== 0 ||
    (grading.vibrance ?? 0) !== 0 ||
    (grading.highlights ?? 0) !== 0 ||
    (grading.shadows ?? 0) !== 0
  );
}
